import {useLazyQuery} from "@apollo/client";
import {useState} from "react";
import {toast} from "react-hot-toast";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import {
  DropdownAction,
  DropdownActionItem,
  DropDownLink,
  DropDownReactLink,
} from "@/components/quickActionsBar/DropdownAction.tsx";
import {DropdownDivider} from "@/components/quickActionsBar/DropdownDivider.tsx";
import {QuickActionsBar} from "@/components/quickActionsBar/QuickActionsBar.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {EditSLAReportResponse, GQL_EDIT_SLA_REPORT, StandardDateRange} from "@/lib/queries/reports.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {deleteSLAReport} from "@/pages/reports/sla/quickActions.ts";
import {SLAReportUpdateDrawer} from "@/pages/reports/sla/SLAReportUpdateDrawer.tsx";

interface SLAReportActionsProps {
  id: number;
  name: string;
  range: StandardDateRange;
}

export const SLAReportActions = ({id, name, range}: SLAReportActionsProps) => {
  const [loadReport, {data, loading}] = useLazyQuery<EditSLAReportResponse>(GQL_EDIT_SLA_REPORT);
  const [showEdit, setShowEdit] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const {user} = useCurrentUserContext();
  const dropdownItems = [<DropDownReactLink text="Preview" to={`/reports/sla/${id}?range=${range}`} />];
  if (user?.has_write_access) {
    dropdownItems.push(
      <DropdownActionItem
        text="Edit Report"
        onClick={async () => {
          await loadReport({variables: {id}});
          setShowEdit(true);
        }}
      />
    );
  }
  dropdownItems.push(
    <DropDownLink
      target="_blank"
      rel="noopener noreferrer"
      text="Download PDF"
      href={`${URL_ORIGINAL_WEBSITE}/reports/sla/${id}/pdf`}
    />
  );
  dropdownItems.push(
    <DropDownLink
      target="_blank"
      rel="noopener noreferrer"
      text="Download XLS"
      href={`${URL_ORIGINAL_WEBSITE}/reports/sla/${id}/xls`}
    />
  );
  if (user?.has_write_access) {
    dropdownItems.push(<DropdownDivider />);
    dropdownItems.push(<DropdownActionItem text="Delete" onClick={() => setShowConfirm(true)} />);
  }

  return (
    <>
      <DeleteActionDialog
        show={showConfirm}
        title="Delete SLA report"
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => {
          const {data} = await deleteSLAReport(id);
          if (data?.response.status === ResponseStatus.SUCCESS) {
            toast.success(`${name} report has been deleted`);
          }
          setShowConfirm(false);
        }}
      >
        Are you sure you want to delete <code>{name}</code> report?
      </DeleteActionDialog>
      {!loading && data && showEdit && (
        <SLAReportUpdateDrawer
          title="Edit SLA Report"
          onClose={() => {
            setShowEdit(false);
          }}
          slaReport={data.slaReport}
          slaReportGlobalLogoUrl={data.slaReportGlobalLogo.logo_url}
          monitoringServiceTypes={data.monitoringServiceTypes}
          tags={data.tags}
        />
      )}
      <QuickActionsBar
        id={`sla-action-${id}`}
        actions={[<DropdownAction id={`sla-dd-${id}`} items={dropdownItems} />]}
      />
    </>
  );
};
